import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ApiCrudSection, {
  type Column,
  type FormField,
  type Row,
} from "../components/ApiCrudSection";
import {
  fetchPeriods,
  createPeriod,
  updatePeriod,
  deletePeriod,
  closePeriod,
} from "../services/payrollService";
import {
  FREQUENCY_OPTIONS,
  PAYROLL_TYPE_OPTIONS,
  frequencyLabel,
  payrollTypeLabel,
  periodStatusLabel,
  PERIOD_STATUS_TONES,
} from "../theme";
import type { PayFrequency, PayrollPeriod, PayrollType } from "../types";
import { useIsStaffAdmin } from "../../../hooks/useIsStaffAdmin";

// ---------------------------------------------------------------------------
// Periodos de nómina y su cierre (AgroCloud#82).
//
// El CFDI de nómina exige declarar la periodicidad (semanal, quincenal…), el
// tipo de nómina (ordinaria/extraordinaria), las fechas del periodo y los días
// pagados: por eso el periodo es una entidad propia y no un filtro de fechas.
//
// EL CIERRE es la regla dura: el backend NO cierra un periodo si alguna de sus
// liquidaciones está incompleta o sin aprobar. Cerrar un periodo con una
// liquidación incompleta sería congelar un sueldo mal pagado.
// ---------------------------------------------------------------------------

function toPayload(values: Record<string, string>): Partial<PayrollPeriod> {
  return {
    name: values.name,
    frequency: (values.frequency || "semimonthly") as PayFrequency,
    payrollType: (values.payrollType || "regular") as PayrollType,
    startDate: values.startDate,
    endDate: values.endDate,
    payDate: values.payDate || null,
    folio: values.folio || null,
    notes: values.notes || null,
  };
}

export default function PeriodsCatalogSection() {
  // #1007: solo quien administra modifica; los demás ven lo suyo en solo lectura.
  const isStaffAdmin = useIsStaffAdmin("payroll");
  const { t } = useTranslation("common");
  const [rows, setRows] = useState<PayrollPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items } = await fetchPeriods();
      setRows(items);
    } catch {
      setError(t("payroll.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleClose = async (id: string) => {
    setActionError(null);
    try {
      await closePeriod(id);
      await load();
    } catch {
      // El backend explica el porqué (liquidaciones incompletas o sin aprobar).
      setActionError(t("payroll.closePeriodError"));
    }
  };

  const badge = (tone: string, label: string) => (
    <span className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${tone}`}>{label}</span>
  );

  const columns: Column[] = [
    { key: "name", label: t("payroll.fields.name") },
    {
      key: "frequency",
      label: t("payroll.fields.frequency"),
      render: (row) => {
        const p = String(row.frequency || "semimonthly");
        return frequencyLabel(t, p);
      },
    },
    {
      key: "payrollType",
      label: t("payroll.fields.payrollType"),
      render: (row) => {
        const v = String(row.payrollType || "regular");
        return payrollTypeLabel(t, v);
      },
    },
    { key: "startDate", label: t("payroll.fields.startDate"), width: "104px" },
    { key: "endDate", label: t("payroll.fields.endDate"), width: "104px" },
    {
      key: "daysPaid",
      label: t("payroll.fields.daysPaid"),
      align: "right",
      render: (row) => {
        const days = row.daysPaid as number | null | undefined;
        return days === null || days === undefined ? "—" : String(days);
      },
    },
    {
      key: "payDate",
      label: t("payroll.fields.payDate"),
      render: (row) => String(row.payDate || "—"),
    },
    {
      key: "status",
      label: t("payroll.fields.periodStatus"),
      render: (row) => {
        const status = String(row.status || "open");
        return badge(
          PERIOD_STATUS_TONES[status] ?? "bg-gray-400/15 text-gray-500",
          periodStatusLabel(t, status),
        );
      },
    },
  ];

  const fields: FormField[] = [
    {
      key: "name",
      label: t("payroll.fields.name"),
      required: true,
      placeholder: "Julio 2026 · 1ª quincena",
    },
    {
      key: "frequency",
      label: t("payroll.fields.frequency"),
      type: "select",
      defaultValue: "semimonthly",
      hint: t("payroll.hints.frequency"),
      options: FREQUENCY_OPTIONS.map((v) => ({ value: v, label: frequencyLabel(t, v) })),
    },
    {
      key: "payrollType",
      label: t("payroll.fields.payrollType"),
      type: "select",
      defaultValue: "regular",
      options: PAYROLL_TYPE_OPTIONS.map((v) => ({ value: v, label: payrollTypeLabel(t, v) })),
    },
    { key: "startDate", label: t("payroll.fields.startDate"), type: "date", required: true },
    { key: "endDate", label: t("payroll.fields.endDate"), type: "date", required: true },
    { key: "payDate", label: t("payroll.fields.payDate"), type: "date" },
    { key: "folio", label: t("payroll.fields.folio") },
    { key: "notes", label: t("payroll.fields.notes"), type: "textarea" },
  ];

  const extraActions = (row: Row) => {
    if (String(row.status || "open") === "closed") return null;
    return (
      <button
        title={t("payroll.closePeriod")}
        aria-label={`close-${row.id}`}
        onClick={() => handleClose(row.id)}
        className="rounded-md p-1.5 text-emerald-500 hover:bg-emerald-500/10"
      >
        <i className="ri-lock-line" />
      </button>
    );
  };

  const banner = actionError ? (
    <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-500">
      <i className="ri-error-warning-line mr-1" />
      {actionError}
    </div>
  ) : null;

  const handleCreate = async (values: Record<string, string>) => {
    await createPeriod(toPayload(values));
    await load();
  };

  const handleUpdate = async (id: string, values: Record<string, string>) => {
    await updatePeriod(id, toPayload(values));
    await load();
  };

  const handleDelete = async (id: string) => {
    await deletePeriod(id);
    await load();
  };

  return (
    <ApiCrudSection
      readOnly={!isStaffAdmin}
      title={t("payroll.menu.periods")}
      subtitle={t("payroll.periodsSubtitle")}
      icon="ri-calendar-2-line"
      addLabel={t("payroll.newPeriod")}
      columns={columns}
      fields={fields}
      rows={rows as unknown as Row[]}
      loading={loading}
      error={error}
      searchKey="name"
      banner={banner}
      extraActions={isStaffAdmin ? extraActions : undefined}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
    />
  );
}
